/**
 * Standalone ExternalRelease detail page with shareable URL.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spin, message, Button, Space, Modal } from 'antd';
import { ArrowLeftOutlined, HistoryOutlined } from '@ant-design/icons';
import { getExternalRelease, getExternalReleaseLogs, updateExternalReleaseUploadStatus, failExternalReleaseFileUpload } from '@/api/externalRelease';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { ExternalReleaseDetail } from '@/components/packages/ExternalReleaseDetail';
import { ExternalReleaseForm } from '@/components/packages/ExternalReleaseForm';
import { OperationLog } from '@/components/logs/OperationLog';
import { useConfigStore } from '@/store/configStore';
import type { ExternalReleaseDetail as ExternalReleaseDetailType, OperationLog as OperationLogType } from '@/types';

const ExternalReleaseDetailPage = () => {
  const { releaseId } = useParams<{ releaseId: string }>();
  const navigate = useNavigate();
  const { getProgress } = useUploadProgressStore();
  const { projects, fetchProjects } = useConfigStore();

  const [releaseData, setReleaseData] = useState<ExternalReleaseDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLogVisible, setIsLogVisible] = useState(false);
  const [logs, setLogs] = useState<OperationLogType[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const currentProject = releaseData?.project
    ? projects.find((p) => p.id === releaseData.project.id)
    : undefined;

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const load = async () => {
      if (!releaseId) return;
      try {
        setLoading(true);
        const data = await getExternalRelease(Number(releaseId));
        setReleaseData(data);
        document.title = `${data.project.name} - ${data.name} - BSP Package Delivery`;

        // 检测卡住的文件：数据库为 uploading/queued，但本 Tab store 无记录且超过 10 分钟没有心跳
        const STUCK_THRESHOLD_MS = 5 * 60 * 1000;
        const now = Date.now();

        const orphans = (data.files || []).filter(f => {
          if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
          const entry = getProgress(`externalrelease-${Number(releaseId)}-file-${f.id}`);
          if (entry && entry.status !== 'failed') return false;
          const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
          return (now - updatedAt) > STUCK_THRESHOLD_MS;
        });
        if (orphans.length > 0) {
          await Promise.allSettled(
            orphans.map(f => failExternalReleaseFileUpload(Number(releaseId), f.id))
          );
          const fresh = await getExternalRelease(Number(releaseId));
          setReleaseData(fresh);
        }
      } catch {
        message.error('加载Momenta释放产物包详情失败');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      document.title = 'BSP Package Delivery - 底软包交付管理系统';
    };
  }, [releaseId]);

  const handleViewLogs = async () => {
    if (!releaseId) return;
    setIsLogVisible(true);
    setLogsLoading(true);
    try {
      const response = await getExternalReleaseLogs(Number(releaseId));
      setLogs(response.results);
    } catch {
      message.error('获取操作日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleFormSuccess = async () => {
    setIsEditMode(false);
    if (!releaseId) return;
    try {
      const data = await getExternalRelease(Number(releaseId));
      setReleaseData(data);
    } catch {
      message.error('刷新详情失败');
    }
  };

  const handleEdit = async () => {
    if (!releaseId) return;
    try {
      const fresh = await getExternalRelease(Number(releaseId));
      setReleaseData(fresh);
      setIsEditMode(true);
    } catch {
      message.error('获取详情失败');
    }
  };

  const handleRefresh = async () => {
    if (!releaseId) return;
    try {
      const data = await getExternalRelease(Number(releaseId));
      setReleaseData(data);
    } catch {
      // silent
    }
  };

  // Auto-poll while release upload is not yet done; reconcile file-level → release-level status
  useEffect(() => {
    if (!releaseData || releaseData.upload_status === 'done' || releaseData.upload_status === 'failed') return;
    const id = setInterval(async () => {
      if (!releaseId) return;
      try {
        const data = await getExternalRelease(Number(releaseId));
        setReleaseData(data);

        // Aggregate file-level statuses to derive correct release-level status
        const files = data.files || [];
        let aggregated: 'uploading' | 'failed' | 'done';
        if (files.some(f => f.upload_status === 'uploading' || f.upload_status === 'queued')) {
          aggregated = 'uploading';
        } else if (files.some(f => f.upload_status === 'failed')) {
          aggregated = 'failed';
        } else {
          aggregated = 'done';
        }

        if (aggregated !== data.upload_status) {
          await updateExternalReleaseUploadStatus(Number(releaseId), aggregated).catch(() => {});
          const fresh = await getExternalRelease(Number(releaseId));
          setReleaseData(fresh);
        }
      } catch {
        // silent
      }
    }, 5000);
    return () => clearInterval(id);
  }, [releaseData?.upload_status, releaseId]);

  if (loading) {
    return <Spin size="large" description="加载中..." fullscreen />;
  }

  if (!releaseData) {
    return (
      <Card>
        <div className="text-center text-gray-500">Momenta释放产物包不存在</div>
      </Card>
    );
  }

  return (
    <div className="p-4">
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              返回
            </Button>
            <span>{releaseData.name} - 详情</span>
          </Space>
        }
      >
        {isEditMode ? (
          <ExternalReleaseForm
            currentProject={currentProject}
            initialData={releaseData}
            isEdit={true}
            erId={releaseData.id}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsEditMode(false)}
          />
        ) : (
          <ExternalReleaseDetail
            release={releaseData}
            onEdit={handleEdit}
            onViewLogs={handleViewLogs}
            onClose={() => navigate(-1)}
            onRefresh={handleRefresh}
          />
        )}
      </Card>

      <Modal
        title={
          <Space>
            <HistoryOutlined />
            操作日志 - {releaseData.name}
          </Space>
        }
        open={isLogVisible}
        onCancel={() => setIsLogVisible(false)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <OperationLog logs={logs} loading={logsLoading} />
      </Modal>
    </div>
  );
};

export default ExternalReleaseDetailPage;
